/**
 * Scenario Complete Component
 * Shows results and feedback after completing a scenario
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ScenarioComplete.css';
import { Award, TrendingUp, CheckCircle, ArrowRight, RotateCcw, FileText, Download, Copy } from 'lucide-react';
import { calculateScenarioScore, getScoreFeedback } from '../../utils/scoring';
import { useProgress } from '../../contexts/ProgressContext';
import { generatePortfolioArtifact } from '../../utils/portfolioArtifact';
import { getNextScenario } from '../../utils/scenarioSequencing';

const ScenarioComplete = ({ scenario, decisions, onRetry, userActions = [], artifacts = [] }) => {
  const navigate = useNavigate();
  const { completeScenario, progress } = useProgress();
  const [score, setScore] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [portfolioArtifact, setPortfolioArtifact] = useState(null);
  const [showPortfolio, setShowPortfolio] = useState(false);
  const [copied, setCopied] = useState(false);
  const [nextScenarioId, setNextScenarioId] = useState(null);
  const [communicationRatings, setCommunicationRatings] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [feedbackError, setFeedbackError] = useState(null);
  const hasInitialized = useRef(false);

  // Always keep next scenario ID in sync with progress (so button is correct)
  useEffect(() => {
    const completedIds = (progress?.completedScenarios || []).map(s => s.id || s);
    const next = getNextScenario(scenario.id, completedIds);
    setNextScenarioId(next);
  }, [scenario.id, progress?.completedScenarios]);

  // Run completion and feedback only once when component mounts for this scenario
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const calculatedScore = calculateScenarioScore(decisions, scenario.successCriteria);
    setScore(calculatedScore);
    setFeedback(getScoreFeedback(calculatedScore));

    const ratings = progress?.communicationRatings?.[scenario.id];
    if (ratings && typeof ratings === 'object') {
      const messages = Object.values(ratings);
      if (messages.length > 0) {
        const totals = messages.reduce((acc, msg) => {
          acc.clarity += msg.clarity || 0;
          acc.conciseness += msg.conciseness || 0;
          acc.collaboration += msg.collaboration || 0;
          acc.justification += msg.justification || 0;
          return acc;
        }, { clarity: 0, conciseness: 0, collaboration: 0, justification: 0 });
        const count = messages.length;
        const averages = {
          clarity: totals.clarity / count,
          conciseness: totals.conciseness / count,
          collaboration: totals.collaboration / count,
          justification: totals.justification / count
        };
        const suggestions = [];
        const threshold = 3.0;
        if (averages.clarity < threshold) suggestions.push({ dimension: 'Clarity', score: averages.clarity, suggestion: 'Be more specific and clear about what you need. Avoid vague language.' });
        if (averages.conciseness < threshold) suggestions.push({ dimension: 'Conciseness', score: averages.conciseness, suggestion: 'Be more brief and to-the-point. Remove unnecessary words.' });
        if (averages.collaboration < threshold) suggestions.push({ dimension: 'Collaboration', score: averages.collaboration, suggestion: 'Ask more questions, acknowledge stakeholder concerns, and show you\'re listening.' });
        if (averages.justification < threshold) suggestions.push({ dimension: 'Justification', score: averages.justification, suggestion: 'Provide more context and reasoning for your requests. Explain the "why".' });
        setCommunicationRatings({ averages, suggestions });
      }
    }

    const artifact = generatePortfolioArtifact(scenario, calculatedScore, decisions, userActions);
    setPortfolioArtifact(artifact);
    completeScenario(scenario.id, calculatedScore, artifact, (artifacts || []).map(a => ({ fileName: a.fileName, taskIndex: a.taskIndex })));

    // Fetch AI-generated feedback and STAR from server (uses saved chat history + uploads)
    const fetchFeedback = async () => {
      setFeedbackLoading(true);
      setFeedbackError(null);
      try {
        const { getScenarioFeedback } = await import('../../utils/api');
        const payload = await getScenarioFeedback(scenario.id, {
          uploads: (artifacts || []).map(a => ({ fileName: a.fileName, taskIndex: a.taskIndex })),
          scenarioSummary: {
            title: scenario.title,
            description: scenario.description,
            currentSituation: scenario.currentSituation,
            companyName: scenario.company?.name,
            tasks: (scenario.tasks || []).map(t => ({ title: t.title, description: t.description }))
          }
        });
        if (payload.feedback && Array.isArray(payload.feedback)) setFeedback(payload.feedback);
        if (payload.starFormat || payload.fullText) {
          setPortfolioArtifact(prev => {
            if (!prev) return prev;
            const next = { ...prev };
            if (payload.starFormat) next.starFormat = payload.starFormat;
            if (payload.fullText) next.fullText = payload.fullText;
            return next;
          });
        }
        if (payload.communicationScore != null && payload.improvementSuggestions) {
          setCommunicationRatings({
            averages: {
              clarity: payload.communicationScore / 25,
              conciseness: payload.communicationScore / 25,
              collaboration: payload.communicationScore / 25,
              justification: payload.communicationScore / 25
            },
            suggestions: payload.improvementSuggestions
          });
        }
      } catch (err) {
        console.error('[ScenarioComplete] Feedback API error:', err);
        setFeedbackError('Personalized feedback could not be loaded. Showing default summary.');
      } finally {
        setFeedbackLoading(false);
      }
    };
    fetchFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount for this scenario
  }, []);

  if (!score) {
    return <div className="scenario-complete-loading">Calculating your score...</div>;
  }

  return (
    <div className="scenario-complete-overlay">
      <div className="scenario-complete-panel">
        <div className="complete-header">
          <Award size={48} className="award-icon" />
          <h1>Scenario Complete!</h1>
          <p className="scenario-name">{scenario.title}</p>
        </div>

        <div className="score-section">
          <div className="total-score">
            <div className="score-value">{score.total.toFixed(0)}</div>
            <div className="score-label">Total Score</div>
          </div>

          <div className="score-breakdown">
            <div className="score-item">
              <span className="score-category">Impact</span>
              <div className="score-bar-container">
                <div 
                  className="score-bar" 
                  style={{ width: `${score.impact}%` }}
                />
              </div>
              <span className="score-value-small">{score.impact.toFixed(0)}</span>
            </div>

            <div className="score-item">
              <span className="score-category">Feasibility</span>
              <div className="score-bar-container">
                <div 
                  className="score-bar" 
                  style={{ width: `${score.feasibility}%` }}
                />
              </div>
              <span className="score-value-small">{score.feasibility.toFixed(0)}</span>
            </div>

            <div className="score-item">
              <span className="score-category">Stakeholder Management</span>
              <div className="score-bar-container">
                <div 
                  className="score-bar" 
                  style={{ width: `${score.stakeholderManagement}%` }}
                />
              </div>
              <span className="score-value-small">{score.stakeholderManagement.toFixed(0)}</span>
            </div>

            <div className="score-item">
              <span className="score-category">Speed</span>
              <div className="score-bar-container">
                <div 
                  className="score-bar" 
                  style={{ width: `${score.speed}%` }}
                />
              </div>
              <span className="score-value-small">{score.speed.toFixed(0)}</span>
            </div>

            <div className="score-item">
              <span className="score-category">Framework Application</span>
              <div className="score-bar-container">
                <div 
                  className="score-bar" 
                  style={{ width: `${score.frameworkApplication}%` }}
                />
              </div>
              <span className="score-value-small">{score.frameworkApplication.toFixed(0)}</span>
            </div>
          </div>
        </div>

        <div className="feedback-section">
          <h3>Feedback</h3>
          {feedbackError && <p className="feedback-error" style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem', marginBottom: '8px' }}>{feedbackError}</p>}
          {feedbackLoading ? (
            <p className="feedback-loading" style={{ color: 'var(--text-secondary)' }}>Loading personalized feedback from your conversations…</p>
          ) : (
          <div className="feedback-list">
            {feedback.map((item, index) => (
              <div key={index} className={`feedback-item ${item.type}`}>
                {item.type === 'strength' ? (
                  <CheckCircle size={16} className="feedback-icon success" />
                ) : (
                  <TrendingUp size={16} className="feedback-icon improvement" />
                )}
                <div className="feedback-content">
                  <div className="feedback-category">{item.category}</div>
                  <div className="feedback-message">{item.message}</div>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>

        {/* Communication Ratings Section */}
        <div className="communication-ratings-section">
          <h3>Communication Ratings</h3>
          {communicationRatings ? (
            <>
              <p className="ratings-description">Your communication was analyzed across 4 dimensions:</p>
            <div className="ratings-grid">
              <div className="rating-item">
                <div className="rating-label">Clarity</div>
                <div className="rating-score">{communicationRatings.averages.clarity.toFixed(1)}/5</div>
                <div className="rating-bar-container">
                  <div 
                    className="rating-bar" 
                    style={{ width: `${(communicationRatings.averages.clarity / 5) * 100}%` }}
                  />
                </div>
              </div>
              <div className="rating-item">
                <div className="rating-label">Conciseness</div>
                <div className="rating-score">{communicationRatings.averages.conciseness.toFixed(1)}/5</div>
                <div className="rating-bar-container">
                  <div 
                    className="rating-bar" 
                    style={{ width: `${(communicationRatings.averages.conciseness / 5) * 100}%` }}
                  />
                </div>
              </div>
              <div className="rating-item">
                <div className="rating-label">Collaboration</div>
                <div className="rating-score">{communicationRatings.averages.collaboration.toFixed(1)}/5</div>
                <div className="rating-bar-container">
                  <div 
                    className="rating-bar" 
                    style={{ width: `${(communicationRatings.averages.collaboration / 5) * 100}%` }}
                  />
                </div>
              </div>
              <div className="rating-item">
                <div className="rating-label">Justification</div>
                <div className="rating-score">{communicationRatings.averages.justification.toFixed(1)}/5</div>
                <div className="rating-bar-container">
                  <div 
                    className="rating-bar" 
                    style={{ width: `${(communicationRatings.averages.justification / 5) * 100}%` }}
                  />
                </div>
              </div>
            </div>
            
              {communicationRatings.suggestions.length > 0 && (
                <div className="improvement-suggestions">
                  <h4>Areas for Improvement</h4>
                  {communicationRatings.suggestions.map((suggestion, index) => (
                    <div key={index} className="suggestion-item">
                      <strong>{suggestion.dimension}</strong>{suggestion.score != null ? ` (${suggestion.score.toFixed(1)}/5): ` : ': '}{suggestion.suggestion}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="ratings-description" style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
              Communication ratings will appear here after you send messages in the scenario.
            </p>
          )}
        </div>

        {/* Portfolio Artifact Section */}
        {portfolioArtifact && (
          <div className="portfolio-section">
            <div className="portfolio-header">
              <FileText size={20} />
              <h3>Portfolio Artifact</h3>
              <span className="portfolio-badge">CV/Interview Ready</span>
            </div>
            
            <button 
              className="action-button secondary portfolio-toggle"
              onClick={() => setShowPortfolio(!showPortfolio)}
            >
              {showPortfolio ? 'Hide' : 'View'} Portfolio Summary
            </button>
            
            {showPortfolio && portfolioArtifact && (
              <div className="portfolio-content">
                <div className="portfolio-star">
                  <h4>STAR Format</h4>
                  <div className="star-item">
                    <strong>Situation:</strong> {portfolioArtifact.starFormat.situation}
                  </div>
                  <div className="star-item">
                    <strong>Task:</strong> {portfolioArtifact.starFormat.task}
                  </div>
                  <div className="star-item">
                    <strong>Action:</strong> {portfolioArtifact.starFormat.action}
                  </div>
                  <div className="star-item">
                    <strong>Result:</strong> {portfolioArtifact.starFormat.result}
                  </div>
                </div>
                
                <div className="portfolio-details">
                  <div className="detail-section">
                    <h5>Key Decisions</h5>
                    <ul>
                      {portfolioArtifact.keyDecisions.map((d, i) => (
                        <li key={i}>{d.decision} <span className="context">({d.context})</span></li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="detail-section">
                    <h5>Tradeoffs</h5>
                    <ul>
                      {portfolioArtifact.tradeoffs.map((t, i) => (
                        <li key={i}>
                          <strong>{t.decision}</strong> - {t.rationale}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="detail-section">
                    <h5>Stakeholder Alignment</h5>
                    <p>{portfolioArtifact.stakeholderAlignment.summary}</p>
                  </div>
                </div>
                
                <div className="portfolio-actions">
                  <button 
                    className="action-button secondary" 
                    onClick={() => {
                      if (portfolioArtifact?.fullText) {
                        navigator.clipboard.writeText(portfolioArtifact.fullText);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }
                    }}
                  >
                    <Copy size={16} />
                    {copied ? 'Copied!' : 'Copy to Clipboard'}
                  </button>
                  <button 
                    className="action-button secondary" 
                    onClick={() => {
                      if (portfolioArtifact?.fullText) {
                        const blob = new Blob([portfolioArtifact.fullText], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `portfolio-${scenario.id}-${Date.now()}.txt`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }
                    }}
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="actions">
          <button className="action-button secondary" onClick={onRetry}>
            <RotateCcw size={18} />
            Retry Scenario
          </button>
          {nextScenarioId ? (
            <button 
              className="action-button primary" 
              onClick={() => navigate(`/scenario/${nextScenarioId}`)}
            >
              Continue to Next Scenario
              <ArrowRight size={18} />
            </button>
          ) : (
            <button 
              className="action-button primary" 
              onClick={() => navigate('/app')}
            >
              Back to Dashboard
              <ArrowRight size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScenarioComplete;





